import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { RolesService, RoleWithPermissionsDto, PermissionDto } from '../../../../services/roles.service';
import { PermissionsService, AllPermissionsResponseDto } from '../../../../services/permissions.service';
import { ToastService } from '../../../../services/toast.service';

@Component({
  selector: 'app-role-form',
  templateUrl: './role-form.component.html',
  styleUrls: ['./role-form.component.scss']
})
export class RoleFormComponent implements OnInit {
  form: FormGroup;
  roleId: string | null = null;
  isEditMode = false;
  loading = false;
  saving = false;
  
  role: RoleWithPermissionsDto | null = null;
  allPermissionsGrouped: AllPermissionsResponseDto | null = null;
  selectedPermissionIds: Set<number> = new Set();
  expandedModules: Set<string> = new Set();

  constructor(
    private fb: FormBuilder,
    private rolesService: RolesService,
    private permissionsService: PermissionsService,
    private router: Router,
    private route: ActivatedRoute,
    private toastService: ToastService
  ) {
    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2), Validators.maxLength(50)]]
    });
  }

  ngOnInit(): void {
    this.roleId = this.route.snapshot.paramMap.get('id');
    this.isEditMode = this.roleId !== null && this.roleId !== 'new';
    
    this.loadPermissions();
    
    if (this.isEditMode && this.roleId) {
      this.loadRole();
    }
  }

  loadPermissions(): void {
    this.permissionsService.getAllPermissionsGrouped().subscribe({
      next: (response) => {
        this.allPermissionsGrouped = response;
      },
      error: (error) => {
        this.toastService.showToast('error', 'Error al cargar los permisos');
        console.error('Error loading permissions:', error);
      }
    });
  }

  loadRole(): void {
    if (!this.roleId) return;
    
    this.loading = true;
    this.rolesService.getRoleById(this.roleId).subscribe({
      next: (role) => {
        this.role = role;
        this.form.patchValue({ name: role.name });
        
        // Cargar permisos seleccionados
        role.permissions.forEach(p => this.selectedPermissionIds.add(p.id));
        
        // Si es rol del sistema, deshabilitar el nombre
        if (role.isSystemRole) {
          this.form.get('name')?.disable();
        }
        
        this.loading = false;
      },
      error: (error) => {
        this.toastService.showToast('error', 'Error al cargar el rol');
        this.loading = false;
        console.error('Error loading role:', error);
      }
    });
  }

  toggleModule(module: string): void {
    if (this.expandedModules.has(module)) {
      this.expandedModules.delete(module);
    } else {
      this.expandedModules.add(module);
    }
  }

  isModuleExpanded(module: string): boolean {
    return this.expandedModules.has(module);
  }

  togglePermission(permissionId: number): void {
    const permission = this.findPermissionById(permissionId);
    
    if (this.selectedPermissionIds.has(permissionId)) {
      // Al deseleccionar, verificar si es READ y hay otros permisos que lo requieren
      if (permission && permission.action.toUpperCase() === 'READ') {
        // Verificar si hay permisos de escritura seleccionados en el mismo módulo
        const hasWritePermissions = this.hasWritePermissionsInModule(permission.module);
        if (hasWritePermissions) {
          this.toastService.showToast('warning', 
            'No puedes quitar el permiso de lectura mientras tengas permisos de escritura en este módulo');
          return;
        }
      }
      this.selectedPermissionIds.delete(permissionId);
    } else {
      // PRIMERO: Si es un permiso de escritura, verificar que READ esté seleccionado
      if (permission && this.isWriteAction(permission.action)) {
        const readPermission = this.getReadPermissionForModule(permission.module);
        if (readPermission && !this.selectedPermissionIds.has(readPermission.id)) {
          // Seleccionar automáticamente READ
          this.selectedPermissionIds.add(readPermission.id);
          this.toastService.showToast('success', 
            `Se agregó automáticamente el permiso de lectura para este módulo`);
        }
      }
      
      // Luego agregar el permiso seleccionado
      this.selectedPermissionIds.add(permissionId);
    }
  }

  /**
   * Verifica si una acción es de escritura (requiere READ)
   */
  private isWriteAction(action: string): boolean {
    return this.WRITE_ACTIONS.includes(action.toUpperCase());
  }

  /**
   * Obtiene el permiso READ de un módulo
   */
  private getReadPermissionForModule(module: string): PermissionDto | null {
    const group = this.allPermissionsGrouped?.groups.find(g => g.module === module);
    if (!group) return null;
    
    return group.permissions.find(p => p.action.toUpperCase() === 'READ') || null;
  }

  /**
   * Acciones que requieren permiso de lectura obligatorio
   */
  private readonly WRITE_ACTIONS = ['CREATE', 'UPDATE', 'DELETE', 'CANCEL', 'APPROVE'];

  /**
   * Busca un permiso por su ID en todos los grupos
   */
  private findPermissionById(permissionId: number): PermissionDto | null {
    if (!this.allPermissionsGrouped) return null;
    
    for (const group of this.allPermissionsGrouped.groups) {
      const permission = group.permissions.find(p => p.id === permissionId);
      if (permission) return permission;
    }
    return null;
  }

  /**
   * Verifica si hay permisos de escritura seleccionados en un módulo
   */
  private hasWritePermissionsInModule(module: string): boolean {
    const group = this.allPermissionsGrouped?.groups.find(g => g.module === module);
    if (!group) return false;
    
    return group.permissions.some(p => 
      this.WRITE_ACTIONS.includes(p.action.toUpperCase()) && 
      this.selectedPermissionIds.has(p.id)
    );
  }

  /**
   * Verifica si un permiso READ es obligatorio (tiene permisos de escritura seleccionados)
   */
  isReadPermissionRequired(permissionId: number): boolean {
    const permission = this.findPermissionById(permissionId);
    if (!permission || permission.action.toUpperCase() !== 'READ') return false;
    
    return this.hasWritePermissionsInModule(permission.module);
  }

  isPermissionSelected(permissionId: number): boolean {
    return this.selectedPermissionIds.has(permissionId);
  }

  selectAllInModule(module: string): void {
    const group = this.allPermissionsGrouped?.groups.find(g => g.module === module);
    if (group) {
      group.permissions.forEach(p => this.selectedPermissionIds.add(p.id));
    }
  }

  deselectAllInModule(module: string): void {
    const group = this.allPermissionsGrouped?.groups.find(g => g.module === module);
    if (group) {
      // Deseleccionar todos los permisos del módulo
      group.permissions.forEach(p => this.selectedPermissionIds.delete(p.id));
    }
  }

  /**
   * Alterna la selección de todos los permisos de un módulo
   */
  toggleAllInModule(module: string): void {
    if (this.isModuleFullySelected(module)) {
      this.deselectAllInModule(module);
    } else {
      this.selectAllInModule(module);
    }
  }

  isModuleFullySelected(module: string): boolean {
    const group = this.allPermissionsGrouped?.groups.find(g => g.module === module);
    if (!group) return false;
    return group.permissions.every(p => this.selectedPermissionIds.has(p.id));
  }

  isModulePartiallySelected(module: string): boolean {
    const group = this.allPermissionsGrouped?.groups.find(g => g.module === module);
    if (!group) return false;
    const selectedCount = group.permissions.filter(p => this.selectedPermissionIds.has(p.id)).length;
    return selectedCount > 0 && selectedCount < group.permissions.length;
  }

  getSelectedCountInModule(module: string): number {
    const group = this.allPermissionsGrouped?.groups.find(g => g.module === module);
    if (!group) return 0;
    return group.permissions.filter(p => this.selectedPermissionIds.has(p.id)).length;
  }

  save(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.saving = true;
    const permissionIds = Array.from(this.selectedPermissionIds);

    if (this.isEditMode && this.roleId) {
      // Actualizar rol existente
      // Primero actualizar el nombre (si no es rol del sistema)
      const updatePromises: Promise<any>[] = [];
      
      if (!this.role?.isSystemRole) {
        updatePromises.push(
          this.rolesService.updateRole(this.roleId, { name: this.form.value.name }).toPromise()
        );
      }

      // Luego asignar permisos
      updatePromises.push(
        this.rolesService.assignPermissions(this.roleId, permissionIds).toPromise()
      );

      Promise.all(updatePromises)
        .then(() => {
          this.toastService.showToast('success', 'Rol actualizado exitosamente');
          this.router.navigate(['/config/roles']);
        })
        .catch(error => {
          this.toastService.showToast('error', 'Error al actualizar el rol');
          console.error('Error updating role:', error);
        })
        .finally(() => {
          this.saving = false;
        });
    } else {
      // Crear nuevo rol
      this.rolesService.createRole({
        name: this.form.value.name,
        permissionIds
      }).subscribe({
        next: () => {
          this.toastService.showToast('success', 'Rol creado exitosamente');
          this.router.navigate(['/config/roles']);
        },
        error: (error) => {
          this.toastService.showToast('error', 'Error al crear el rol');
          console.error('Error creating role:', error);
          this.saving = false;
        }
      });
    }
  }

  cancel(): void {
    this.router.navigate(['/config/roles']);
  }
}

